import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import Layout from '../components/Layout';
import Meta from '../components/Meta';
import Container from '../components/Container';
import Breadcrumb from '../components/Breadcrumb';
import Intro from '../components/Intro';
import Dropzone from '../components/Dropzone';
import Features from '../components/Features';
import Faq from '../components/Faq';
import ErrorModal from '../components/ErrorModal';
import useUpload from '../utils/useUpload';
import usePdfAccept from '../utils/usePdfAccept';
import { apiUrl } from '../utils/apiRequest';
import useApiRequest from '../utils/useApiRequest';
import getErrorName from '../utils/getErrorName';
import s, { UnlockResponseStruct } from '../utils/struct';
import { sendEvent } from '../utils/analytics';
import { Result } from '../components/Result';

const PageUnlockPdf = () => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'unlock-pdf']);
  const { basePath } = useRouter();

  const [{ step, result }, setState] = useState({
    step: 1,
    result: null,
  });

  const handleGoToStep2 = useCallback((result) => {
    setState((state) => ({
      ...state,
      step: 2,
      result,
    }));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  let content;

  if (step === 1) {
    content = (
      <Step1
        onNextStep={handleGoToStep2}
      />
    );
  } else if (step === 2) {
    content = (
      <Step2
        result={result}
      />
    );
  }

  return (
    <Layout className="page-convert">
      <Meta
        title={t('unlock-pdf:meta.title')}
        description={t('unlock-pdf:meta.description')}
      />
      {content}
      <Features
        content={t('unlock-pdf:features.content', { returnObjects: true })}
        style={{ backgroundImage: `url(${basePath}/images/features-3.svg)` }}
      />
      <Faq items={t('unlock-pdf:faq.items', { returnObjects: true })} />
    </Layout>
  );
};

const Step1 = ({ onNextStep }) => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'unlock-pdf']);
  const { basePath } = useRouter();
  const [files, setFiles] = useState([]);

  const {
    processing,
    fileWithError,
    handleAccepted,
    errorModal: dropzoneErrorModal,
  } = usePdfAccept(files, setFiles, false);

  const {
    sendApiRequest,
    processing: processingUnlock,
    error: unlockError,
  } = useApiRequest({ url: '/api/pdf/unlock' });

  const hasFileWithError = !!fileWithError;
  const { upload, uploading, error: uploadError } = useUpload();
  const [showUploadError, setShowUploadError] = useState(false);
  const [showUnlockError, setShowUnlockError] = useState(false);

  const unlock = useCallback((files) => {
    // temporary lock to 1 file
    const file = files[0];

    sendApiRequest({
      data: {
        name: file.file.name,
        filename: file.hash,
        password: file.password,
      },
      validateResponse(response) {
        s.assert(response.data, UnlockResponseStruct);
      },
      onSuccess(response, time) {
        sendEvent({
          category: 'unlock-pdf',
          action: 'success',
          label: `${time}:${files.length}`,
        });

        let fileType;
        let fileUrl;
        let fileName;
        let fileSize;

        if (response.data.zip) {
          const { url, size } = response.data.zip;

          fileType = 'zip';
          fileUrl = url;
          fileName = 'files-unlock-pdf.zip';
          fileSize = size;
        } else {
          // TODO
          // const { url, size } = response.data.result[0];
          const url = response.data.result;
          const size = file.file.size;

          fileType = 'pdf';
          fileUrl = url;
          fileName = file.file.name;

          fileSize = size;
        }

        onNextStep({
          fileType,
          fileName,
          fileUrl: apiUrl(fileUrl),
          fileSize,
          compressable: false, // TODO: make compressable
        });
      },
      onError(error, time) {
        sendEvent({
          category: 'unlock-pdf',
          action: 'error',
          label: `${getErrorName(error)}:${time}:${files.length}`,
        });
      },
    });
  }, [sendApiRequest, onNextStep]);

  const handleUploadErrorModalHide = useCallback(() => {
    setShowUploadError(false);
  }, []);

  const handleUnlockErrorModalHide = useCallback(() => {
    setShowUnlockError(false);
  }, []);

  let errorModal = dropzoneErrorModal;

  if (!errorModal) {
    if (uploadError) {
      errorModal = (
        <ErrorModal
          title={t('convert-and-edit:upload-error-modal.title')}
          text={t('convert-and-edit:upload-error-modal.text')}
          show={showUploadError}
          onHide={handleUploadErrorModalHide}
        />
      );
    } else if (unlockError) {
      errorModal = (
        <ErrorModal
          title={t('convert-and-edit:processing-error-modal.title')}
          text={t('convert-and-edit:processing-error-modal.text')}
          show={showUnlockError}
          onHide={handleUnlockErrorModalHide}
        />
      );
    }
  }

  useEffect(() => {
    if (hasFileWithError) {
      return;
    }

    const filesToUpload = files.map(({ file }) => file);

    if (!filesToUpload.length) {
      return;
    }

    upload(filesToUpload, (uploadedFiles) => {
      unlock(files.map((file, i) => ({
        ...file,
        hash: uploadedFiles[i].hash,
      })));
    });
  }, [files, hasFileWithError, upload, unlock]);

  useEffect(() => {
    if (uploadError) {
      setShowUploadError(true);
    }
  }, [uploadError]);

  useEffect(() => {
    if (unlockError) {
      setShowUnlockError(true);
    }
  }, [unlockError]);

  return (
    <>
      <Container className="my-xs-3">
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            {t('common:nav.index')}
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            {t('common:nav.unlock-pdf')}
          </Breadcrumb.Item>
        </Breadcrumb>
      </Container>
      <Intro
        title={t('unlock-pdf:intro.title')}
        text={t('unlock-pdf:intro.text')}
        icon={<img src={`${basePath}/images/intro-2.svg`} width="214" alt="" />}
      />
      <Container className="my-xs-3 my-sm-4 my-md-5 my-lg-6">
        <Dropzone
          type="pdf"
          disabled={processing || hasFileWithError || uploading || processingUnlock}
          onAccepted={handleAccepted}
        />
      </Container>
      {errorModal}
    </>
  );
};

const Step2 = ({ result }) => {
  const { t } = useTranslation(['common', 'convert-and-edit', 'unlock-pdf']);
  const actions = {
    edit: true,
    rotate: true,
    unlock: true,
    merge: true,
    split: true,
    compress: true,
    pdf2word: true,
    pdf2excel: true,
    pdf2jpg: true,
    pdf2png: true,
    pdf2ppt: true,
  }
  return (<Result result={result} t={t} actions={actions} route='/unlock-pdf'/>);
};

Step1.propTypes = {
  onNextStep: PropTypes.func.isRequired,
};

Step2.propTypes = {
  result: PropTypes.shape({
    fileType: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    fileUrl: PropTypes.string.isRequired,
    fileSize: PropTypes.number.isRequired,
  }).isRequired,
};

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...await serverSideTranslations(locale, [
      'common',
      'convert-and-edit',
      'unlock-pdf',
    ]),
  },
});

export default PageUnlockPdf;
